import { Box, Stack, SxProps, Theme, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { IconButton, Paper, SizingMultiplier, Typography } from '@ocx/ui';
import { LocationArrow2Icon } from '@ocx/ui-icons';
import { Coordinates } from '../../modules/stores/types';
import { OfferLocationsCarouselItemOnClickParams } from './types';

const TEXT_OVERFLOW_ELLIPSIS_SX: SxProps = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const iconSx: SxProps<Theme> = (theme: Theme) => ({
  ml: SizingMultiplier.md,
  color: theme.palette.Receipts.offerLinkColor,
  backgroundColor: theme.palette.Receipts.iconBackground,
  borderRadius: theme.shape.borderRadius / 2,
});

type OfferLocationsCarouselItemProps = {
  isFirst: boolean;
  isLast: boolean;
  hasMultipleItems: boolean;
  uuid: string;
  name: string;
  address: string;
  coordinates: Coordinates;
  onClick?: (params: OfferLocationsCarouselItemOnClickParams) => void;
};

const FIRST_ITEM_WIDTH_PERCENT = '90%';
// Margin compensates for negative wrapper margin and inner padding
const CAROUSEL_ITEM_MARGIN = SizingMultiplier.lg + SizingMultiplier.md;

const getCarouselItemMaxWidth = (
  theme: Theme,
  hasMultipleItems: boolean,
  isFirstItem: boolean,
  isLastItem: boolean,
) => {
  if (!hasMultipleItems && isFirstItem) {
    // To make first item full width if it is only one item in the array
    return '100%';
  }

  if (hasMultipleItems && (isFirstItem || isLastItem)) {
    // First/last: slightly narrower to show there are more items user can scroll to
    return `calc(${FIRST_ITEM_WIDTH_PERCENT} - ${theme.spacing(CAROUSEL_ITEM_MARGIN)})`;
  }

  // Default: full width minus left and right margins
  return `calc(100% - ${theme.spacing(CAROUSEL_ITEM_MARGIN * 2)})`;
};

export const OfferLocationsCarouselItem = ({
  isFirst,
  isLast,
  hasMultipleItems,
  uuid,
  name,
  address,
  coordinates,
  onClick,
}: OfferLocationsCarouselItemProps) => {
  const theme = useTheme();

  const handleOnClick = useCallback(() => {
    onClick?.({ uuid, coordinates });
  }, [coordinates, onClick, uuid]);

  const maxWidth = useMemo(
    () => getCarouselItemMaxWidth(theme, hasMultipleItems, isFirst, isLast),
    [hasMultipleItems, isFirst, isLast, theme],
  );

  return (
    <Box maxWidth={maxWidth}>
      <Paper
        ml={isFirst ? CAROUSEL_ITEM_MARGIN : 0}
        mr={isLast ? CAROUSEL_ITEM_MARGIN : 0}
        sx={{ border: `1px solid ${theme.palette.background.box}` }}>
        <Stack direction="row" justifyContent="space-between" flexWrap="nowrap">
          <Box sx={TEXT_OVERFLOW_ELLIPSIS_SX}>
            <Typography variant="subtitle1" sx={TEXT_OVERFLOW_ELLIPSIS_SX}>
              {name}
            </Typography>

            <Typography variant="body2" sx={TEXT_OVERFLOW_ELLIPSIS_SX}>
              {address}
            </Typography>
          </Box>

          <IconButton onClick={handleOnClick} sx={iconSx}>
            <LocationArrow2Icon />
          </IconButton>
        </Stack>
      </Paper>
    </Box>
  );
};
